/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { HttpClient, HttpParams } from '@angular/common/http';
import { AbstractAgentManagedHttpService } from '@src/app/shared/abstract-agent-managed-http.service';
import { MessageService } from '@src/app/shared/message.service';
import { ServiceRequest } from '@src/app/model/serviceRequest';
import { AiLog } from '@src/app/model/ai-log';
import { AbstractObject, uuid } from '@src/app/model/abstract-object';
import { catchError, map } from 'rxjs/operators';
import { Observable, of, throwError } from 'rxjs';
import { DateHelper } from '@src/app/shared/dateHelper';
import { FieldSet, LAYOUT_OPTIONS } from '@src/app/shared/form/field-set/field-set.component';
import { FieldSetDialogComponent, FieldSetDialogOptions } from '@src/app/shared/dialogs/field-set-dialog/field-set-dialog.component';
import { FormDateComponent } from '@src/app/shared/form/form-date/form-date.component';
import { notBeforeToday, required } from '@src/app/shared/validators';

/**
 * A newly created request. mailSent / mailError are only there when a supplier mail was asked for:
 * the request is created whether or not its mail got out, so the outcome rides back on the request
 * rather than replacing it.
 */
export interface CreatedRequest extends ServiceRequest {
    mailSent?: boolean;
    mailError?: string;
}

/**
 * The answer to "is there an AI proposal for this request?". log is null more often than not, and
 * reason says which of the ordinary causes it was - aiOff, notCase, systemRaised, notOpen, answered,
 * failed - so a page that shows no dialog can still say why. canSend is whether anyone outside the
 * agency follows the request, so a reply would reach someone.
 */
export interface AiProposalResult {
    log: AiLog | null;
    reason: string;
    message: string;
    canSend: boolean;
}

/** What the AI review dialog posts back - the answer as the reviewer left it. */
export interface AiProposalAnswer {
    aiLogId: uuid;
    srTopic?: string;
    srClass?: string;
    srArea?: uuid;
    srCore?: uuid;
    srStatus: string;
    comment: string;
}

/** What the close dialogs post - a request id plus the answers, not a ServiceRequest. */
export interface CloseRequestData {
    requestId: uuid;
    teamId?: string;
    srCloseStatus?: string;
    closeText?: string;
    closeWorkTime?: number;
    duplicateId?: uuid;
    srTopic?: string;
    srClass?: string;
    /** Work orders only - the server rejects it on a case. */
    finalStatus?: uuid;
}

@Injectable({
    providedIn: 'root'
})
export class RequestService extends AbstractAgentManagedHttpService {
    protected http = inject(HttpClient);
    protected messageService = inject(MessageService);
    private dialog = inject(MatDialog);


    protected get baseUrl() {
        return this.getAjaxPath('crm/requests');
    }
    protected cache: ServiceRequest[];
    protected typeString = 'ServiceRequest';

    getOpen() {
        return this.get(false, new HttpParams().set('srStatus', 'open'));
    }

    /*
    getOmcOpen(omcId: uuid) {
        return this.get(false, new HttpParams().set('srStatus', 'open').set('omcId', omcId));
    }
*/

    /**
     * Rethrows rather than swallowing: a work order close can follow a purchase that has already
     * been saved, so a caller has to be able to tell a failed close from a successful one.
     * Both callers report the error themselves.
     */
    closeRequest(data: CloseRequestData) {
        const url = `${this.writeUrl(data as unknown as AbstractObject)}/${data['requestId']}/close`;
        return this.http.post(url, data, this.getSimpleOptions(null)).pipe( map((r: ServiceRequest) => {
            this.updateCachedItem(r);
            return r;
        }), catchError(err => {
            console.error(err);
            return throwError(() => err);
        }));
    }

    /**
     * The AI's answer for this request, or null where there is none to have. Addressed at the
     * request's own tenant like every other call here - the answer is built from that OMC's areas,
     * notices and documents, and from the agency's list the page is standing in the agency.
     */
    getAiProposal(sr: ServiceRequest): Observable<AiProposalResult> {
        const url = `${this.writeUrl(sr)}/${sr.id}/aiProposal`;
        return this.http.get<AiProposalResult>(url, this.getSimpleOptions(null)).pipe(
            catchError(err => {
                console.error(err);
                // A page must still open when the assistant does not answer.
                return of({ log: null, reason: 'error', message: err?.message ?? 'Request failed', canSend: false });
            }));
    }

    acceptAiProposal(sr: ServiceRequest, answer: AiProposalAnswer) {
        return this.postAiAnswer(`${this.writeUrl(sr)}/${sr.id}/aiProposal`, answer);
    }

    saveAiProposalWithoutSending(sr: ServiceRequest, answer: AiProposalAnswer) {
        return this.postAiAnswer(`${this.writeUrl(sr)}/${sr.id}/aiProposal/save`, answer);
    }

    private postAiAnswer(url: string, answer: AiProposalAnswer) {
        return this.http.post(url, answer, this.getSimpleOptions(null)).pipe(
            map((r: ServiceRequest) => {
                this.updateCachedItem(r);
                return r;
            }), catchError(err => {
                console.error(err);
                return throwError(() => err);
            }));
    }

    rejectAiProposal(sr: ServiceRequest, aiLogId: uuid) {
        const url = `${this.writeUrl(sr)}/${sr.id}/aiProposal/reject`;
        return this.http.post(url, { aiLogId }, this.getSimpleOptions(null)).pipe(
            map((r: ServiceRequest) => {
                this.updateCachedItem(r);
                return r;
            }), catchError(err => {
                this.messageService.showError(err, 'Rejecting AI answer');
                return of(sr);
            }));
    }

    reopenRequest(sr: ServiceRequest, reason: string) {
        const url = `${this.writeUrl(sr)}/${sr.id}/reopen`;
        const data = { requestId: sr.id, reason };
        return this.http.post(url, data, this.getSimpleOptions(null)).pipe(map((r: ServiceRequest) => {
            this.updateCachedItem(r);
            return r;
        }), catchError(err => {
            this.messageService.showError(err,'reopening request');
            console.error(err);
            return of(sr); // Return original sr on error
        }));
    }

    acceptRequest(sr: ServiceRequest) {
        const url = `${this.writeUrl(sr)}/${sr.id}/accept`;
        const data = { requestId: sr.id };
        return this.http.post(url, data, this.getSimpleOptions(null)).pipe(map((r: ServiceRequest) => {
            this.updateCachedItem(r);
            return r;
        }), catchError(err => {
            this.messageService.showError(err,'accepting request');
            console.error(err);
            return of(sr); // Return original sr on error
        }));
    }

    approveRequest(sr: ServiceRequest) {
        const url = `${this.writeUrl(sr)}/${sr.id}/approve`;
        const data = { requestId: sr.id };
        return this.http.post(url, data, this.getSimpleOptions(null)).pipe(map((r: ServiceRequest) => {
            this.updateCachedItem(r);
            return r;
        }), catchError(err => {
            this.messageService.showError(err,'approvomg request');
            console.error(err);
            return of(sr); // Return original sr on error
        }));
    }
    
    unapproveRequest(sr: ServiceRequest, reason: string) {
        const url = `${this.writeUrl(sr)}/${sr.id}/unapprove`;
        const data = { requestId: sr.id, reason };
        return this.http.post(url, data, this.getSimpleOptions(null)).pipe(map((r: ServiceRequest) => {
            this.updateCachedItem(r);
            return r;
        }), catchError(err => {
            this.messageService.showError(err,'unapproving request');
            console.error(err);
            return of(sr); // Return original sr on error
        }));
    }

    /**
     * Asks when a reply is expected and, on OK, raises the request's "Check for response" follow-up
     * action for that date. Cancelling leaves the request exactly as it was. Offered after anything
     * that writes to a contact about the request - a mail to a follower or supplier, or a comment.
     */
    promptExpectResponse(sr: ServiceRequest): Observable<ServiceRequest> {
        if (!sr || sr.srStatus !== ServiceRequest.srOpen.id) {
            return of(sr);
        }
        const expectBy = FormDateComponent.make('Expect a response for this by', 'expectBy', {
            validators: [required, notBeforeToday]
        });
        const fieldSet = new FieldSet({ fields: [expectBy], formLayout: LAYOUT_OPTIONS.simpleDialog });
        fieldSet.setValue({ id: sr.id, expectBy: DateHelper.today(7) } as AbstractObject);

        const data = {
            fieldSet,
            saveText: $localize`OK`,
            saveFn: (entered: AbstractObject) => this.waitForResponse(sr, entered['expectBy']),
            title: $localize`Wait for a response?`,
        } as FieldSetDialogOptions;

        return this.dialog.open(FieldSetDialogComponent, { data }).afterClosed()
            .pipe(map((updated: ServiceRequest) => updated ?? sr));
    }

    waitForResponse(sr: ServiceRequest, expectBy: string) : Observable<ServiceRequest>{
        const url = `${this.writeUrl(sr)}/${sr.id}/waiting`;
        const data = { requestId: sr.id, expectBy };
        return this.http.post(url, data, this.getSimpleOptions(null)).pipe(map((r: ServiceRequest) => {
            this.updateCachedItem(r);
            return r;
        }), catchError(err => {
            this.messageService.showError(err, 'creating the follow up action');
            console.error(err);
            return of(sr); // Return original sr on error
        }));
    }    

    // mode = send | test | attach. 'send' mails the suppliers; 'test' mails a copy to the current
    // user; 'attach' saves the content as a PDF against the request. Mirrors PersonService.sendMail.
    sendSupplierMails(sr: ServiceRequest, content: string, mode: 'send' | 'test' | 'attach',
                      suppliers: { id: uuid }[], attachments: AbstractObject[]) {
        const url = `${this.writeUrl(sr)}/${sr.id}/sendSuppliers`;
        // Send supplier ids only — the server looks each one up and mails the address held on record,
        // so it never trusts a client-supplied email or mails an unknown recipient.
        const data = { requestId: sr.id, content, mode, suppliers: suppliers.map(s => s.id), attachments };
        return this.http.post(url, data, this.getSimpleOptions(null));
    }

    /**
     * The new request stepper's one save - the standard create, which also copies the parent's files
     * and mails the supplier when the payload asks for it. showMsg is off because the stepper says
     * its own piece about the mail; writeUrl sends it to the OMC named by the payload's teamId.
     */
    postNewRequest(request: AbstractObject): Observable<CreatedRequest> {
        return this.post<CreatedRequest>(request as CreatedRequest, false);
    }

    previewSupplierMail(sr: ServiceRequest, content: string, attachments: AbstractObject[]): Observable<{ html: string }> {
        const url = `${this.writeUrl(sr)}/${sr.id}/sendSuppliers`;
        const data = { requestId: sr.id, content, mode: 'preview', attachments };
        return this.http.post<{ html: string }>(url, data, this.getSimpleOptions(null)).pipe(
            catchError(error => {
                this.messageService.showError(error, 'Previewing Supplier Mail');
                return of(null);
            })
        );
    }
}
